import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { darkColors } from '../constants/design_system/colors';

type ThemePreference = 'light' | 'system' | 'dark';
type ResolvedTheme = 'light' | 'dark';

interface ThemeStore {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

// Flatten { neutral: { 100: '#hex' } } → [['--color-neutral-100', '#hex'], ...]
function flattenToVars(colors: Record<string, Record<string, string>>): [string, string][] {
  const result: [string, string][] = [];
  for (const [group, shades] of Object.entries(colors)) {
    for (const [shade, hex] of Object.entries(shades)) {
      result.push([`--color-${group}-${shade}`, hex]);
    }
  }
  return result;
}

const getSystemTheme = (): ResolvedTheme => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  return preference === 'system' ? getSystemTheme() : preference;
};

const applyTheme = (theme: ResolvedTheme) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);

  const vars = flattenToVars(darkColors);

  if (theme === 'dark') {
    for (const [name, value] of vars) {
      root.style.setProperty(name, value);
    }
  } else {
    for (const [name] of vars) {
      root.style.removeProperty(name);
    }
  }
};

let mediaQueryCleanup: (() => void) | null = null;

const setupMediaQueryListener = () => {
  if (mediaQueryCleanup) return;

  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = () => {
    const state = useThemeStore.getState();
    if (state.preference === 'system') {
      const resolved = getSystemTheme();
      applyTheme(resolved);
      useThemeStore.setState({ resolvedTheme: resolved });
    }
  };

  mql.addEventListener('change', handler);
  mediaQueryCleanup = () => mql.removeEventListener('change', handler);
};

export const useThemeStore = create<ThemeStore>()(
  devtools(
    persist(
      (set) => ({
        preference: 'dark',
        resolvedTheme: 'dark',
        setPreference: (preference: ThemePreference) => {
          const resolved = resolveTheme(preference);
          applyTheme(resolved);
          set({ preference, resolvedTheme: resolved });
        },
      }),
      {
        name: 'theme-storage',
        version: 2,
        migrate: (persisted, version) => {
          if (version === 0) {
            // Old shape: { theme: 'light' | 'dark' } → new shape
            const old = persisted as Record<string, unknown> | null;
            const oldTheme = old?.theme as string | undefined;
            return {
              preference: oldTheme === 'dark' ? ('dark' as const) : ('light' as const),
              resolvedTheme: oldTheme === 'dark' ? ('dark' as const) : ('light' as const),
            };
          }
          if (version === 1) {
            // Force all existing users to dark mode
            return { preference: 'dark' as const };
          }
          return persisted as { preference: ThemePreference };
        },
        partialize: (state) => ({ preference: state.preference }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            const resolved = resolveTheme(state.preference);
            state.resolvedTheme = resolved;
            applyTheme(resolved);
            setupMediaQueryListener();
          }
        },
      }
    ),
    { name: 'theme-store' }
  )
);
